import type { SaleInput, SaleReturnInput } from '@najah/core'
import { api, ApiError } from '../api'
import { getDb, getMeta, setMeta, pendingOutbox, markSynced, markFailed, markRetry, countPending } from './localDb'
import { getDeviceKey, isTauri } from './platform'

interface PullResponse {
  cursor: string
  hasMore: boolean
  serverTime: string
  categories: {
    id: string; nameAr: string; parentId: string | null; sortOrder: number
    isActive: boolean; deletedAt: string | null
  }[]
  products: {
    id: string; sku: string; barcode: string | null; nameAr: string; nameFr: string | null
    searchKey: string; type: string; categoryId: string | null; unit: string
    costPrice: number; retailPrice: number; taxRate: number; minStock: number
    trackStock: boolean; isActive: boolean; deletedAt: string | null
  }[]
  levels: { productId: string; quantity: number }[]
  settings: Record<string, unknown> | null
}

interface PushResponse {
  accepted: { id: string; reference: string }[]
  duplicates: string[]
  failed: { id: string; code: string; message: string }[]
  cursor: string
}

export interface DeviceInfo {
  deviceKey: string
  deviceCode: string
  storeCode: string
  storeName: string
}

const CURSOR_KEY = 'sync.cursor'
const DEVICE_INFO_KEY = 'sync.device'
const LAST_SYNC_KEY = 'sync.lastAt'

/** أخطاء الخادم التي لا فائدة من إعادة محاولتها — البيانات نفسها مرفوضة */
const PERMANENT_ERRORS = new Set([
  'TOTAL_MISMATCH',
  'PRODUCT_NOT_FOUND',
  'VALIDATION_ERROR',
  'ITEM_NOT_IN_SALE',
  'RETURN_EXCEEDS_SOLD',
  'SALE_VOIDED',
])

export async function registerDevice(): Promise<DeviceInfo> {
  const deviceKey = getDeviceKey()
  const info = await api<DeviceInfo & { cursor: string }>('/sync/device', {
    method: 'POST',
    body: { deviceKey, nameAr: 'نقطة بيع', platform: isTauri() ? 'desktop' : 'web' },
  })

  if (isTauri()) {
    await setMeta(DEVICE_INFO_KEY, JSON.stringify(info))
  }
  return info
}

export async function getCachedDeviceInfo(): Promise<DeviceInfo | null> {
  if (!isTauri()) return null
  const raw = await getMeta(DEVICE_INFO_KEY)
  return raw ? (JSON.parse(raw) as DeviceInfo) : null
}

/** يسحب تغييرات الكتالوج والأرصدة إلى SQLite حتى ينفد ما بعد المؤشّر */
export async function pullCatalog(onProgress?: (n: number) => void): Promise<{ pulled: number; cursor: string }> {
  const db = await getDb()
  let cursor = (await getMeta(CURSOR_KEY)) ?? '0'
  let pulled = 0

  for (let page = 0; page < 200; page++) {
    const res = await api<PullResponse>('/sync/pull', {
      query: { cursor, deviceKey: getDeviceKey() },
    })

    for (const c of res.categories) {
      await db.execute(
        `INSERT INTO categories (id, nameAr, parentId, sortOrder, isActive, deletedAt)
         VALUES ($1,$2,$3,$4,$5,$6)
         ON CONFLICT(id) DO UPDATE SET
           nameAr=excluded.nameAr, parentId=excluded.parentId, sortOrder=excluded.sortOrder,
           isActive=excluded.isActive, deletedAt=excluded.deletedAt`,
        [c.id, c.nameAr, c.parentId, c.sortOrder, c.isActive ? 1 : 0, c.deletedAt],
      )
    }

    for (const p of res.products) {
      await db.execute(
        `INSERT INTO products (id, sku, barcode, nameAr, nameFr, searchKey, type, categoryId, unit,
                               costPrice, retailPrice, taxRate, minStock, trackStock, isActive, deletedAt)
         VALUES ($1,$2,$3,$4,$5,$6,$7,$8,$9,$10,$11,$12,$13,$14,$15,$16)
         ON CONFLICT(id) DO UPDATE SET
           sku=excluded.sku, barcode=excluded.barcode, nameAr=excluded.nameAr, nameFr=excluded.nameFr,
           searchKey=excluded.searchKey, type=excluded.type, categoryId=excluded.categoryId, unit=excluded.unit,
           costPrice=excluded.costPrice, retailPrice=excluded.retailPrice, taxRate=excluded.taxRate,
           minStock=excluded.minStock, trackStock=excluded.trackStock, isActive=excluded.isActive,
           deletedAt=excluded.deletedAt`,
        [
          p.id, p.sku, p.barcode, p.nameAr, p.nameFr, p.searchKey, p.type, p.categoryId, p.unit,
          p.costPrice, p.retailPrice, p.taxRate, p.minStock, p.trackStock ? 1 : 0, p.isActive ? 1 : 0, p.deletedAt,
        ],
      )
    }

    for (const l of res.levels) {
      await db.execute(
        `INSERT INTO inventory_levels (productId, quantity) VALUES ($1,$2)
         ON CONFLICT(productId) DO UPDATE SET quantity=excluded.quantity`,
        [l.productId, l.quantity],
      )
    }

    pulled += res.categories.length + res.products.length + res.levels.length
    onProgress?.(pulled)

    // المؤشّر يُحفظ بعد كتابة الصفحة لا قبلها: لو انقطع الاتصال في المنتصف
    // نُعيد من آخر صفحة اكتملت فعلاً بدل تخطّي بيانات.
    cursor = res.cursor
    await setMeta(CURSOR_KEY, cursor)

    if (!res.hasMore) break
  }

  return { pulled, cursor }
}

export async function pushOutbox(): Promise<{ sent: number; failed: number; duplicates: number }> {
  const rows = await pendingOutbox(100)
  if (!rows.length) return { sent: 0, failed: 0, duplicates: 0 }

  const sales: SaleInput[] = []
  const returns: SaleReturnInput[] = []
  for (const row of rows) {
    const payload = JSON.parse(row.payload)
    if (row.kind === 'sale') sales.push(payload)
    else returns.push(payload)
  }

  let res: PushResponse
  try {
    res = await api<PushResponse>('/sync/push', {
      method: 'POST',
      body: { deviceKey: getDeviceKey(), sales, returns },
    })
  } catch (err) {
    // فشل الدفعة كلها (شبكة أو خادم): لا نُعلّم شيئاً فاشلاً — نُعيد لاحقاً
    const message = err instanceof ApiError ? err.message : 'تعذّر الاتصال'
    for (const row of rows) await markRetry(row.id, message)
    throw err
  }

  await markSynced([...res.accepted.map((a) => a.id), ...res.duplicates])

  for (const f of res.failed) {
    if (PERMANENT_ERRORS.has(f.code)) {
      await markFailed(f.id, `${f.code}: ${f.message}`)
    } else {
      await markRetry(f.id, `${f.code}: ${f.message}`)
    }
  }

  return { sent: res.accepted.length, failed: res.failed.length, duplicates: res.duplicates.length }
}

/**
 * السحب يكتب أرصدة الخادم فوق الأرصدة المحلية. لو بقيت فواتير في الطابور
 * (فشل دفع جزئي) فأرصدة الخادم لا تعرفها بعد، فيرتفع الرصيد المعروض للكاشير
 * فوق الواقع. نُعيد خصم ما ينتظر في الطابور بعد كل سحب.
 */
async function reapplyPendingToLevels(): Promise<number> {
  const rows = await pendingOutbox(500)
  if (!rows.length) return 0

  const db = await getDb()
  let applied = 0

  for (const row of rows) {
    if (row.kind !== 'sale') continue
    const sale = JSON.parse(row.payload) as SaleInput
    for (const item of sale.items) {
      await db.execute(
        `INSERT INTO inventory_levels (productId, quantity) VALUES ($1, $2)
         ON CONFLICT(productId) DO UPDATE SET quantity = quantity - $3`,
        [item.productId, -item.quantity, item.quantity],
      )
    }
    applied++
  }
  return applied
}

export interface SyncResult {
  pushed: number
  pulled: number
  failed: number
  at: string
}

/**
 * مزامنة كاملة: الدفع أولاً ثم السحب.
 * الترتيب مقصود — لو سحبنا أولاً لظهرت الأرصدة قبل أن تصل مبيعاتنا،
 * فتُعرَض للكاشير كميات أعلى مما يملك فعلاً.
 */
/**
 * يضمن تسجيل الجهاز على الخادم قبل أول مزامنة.
 * بدونه يُرسل السحب مفتاح جهاز لا وجود له، فيفشل حفظ المؤشّر على الخادم
 * ويبقى الجهاز مجهولاً في لوحة الإدارة رغم أنه يسحب البيانات ويبيع.
 */
async function ensureDeviceRegistered(): Promise<void> {
  if (!isTauri()) return
  const cached = await getCachedDeviceInfo()
  if (cached) return
  await registerDevice()
}

export async function syncNow(): Promise<SyncResult> {
  await ensureDeviceRegistered()
  const push = await pushOutbox()
  const pull = await pullCatalog()
  await reapplyPendingToLevels()
  const at = new Date().toISOString()
  await setMeta(LAST_SYNC_KEY, at)
  return { pushed: push.sent, pulled: pull.pulled, failed: push.failed, at }
}

export async function getSyncStatus() {
  if (!isTauri()) {
    return { pending: 0, failed: 0, lastSyncAt: null as string | null, offlineCapable: false }
  }
  const [counts, lastSyncAt] = await Promise.all([countPending(), getMeta(LAST_SYNC_KEY)])
  return { ...counts, lastSyncAt, offlineCapable: true }
}
